import React from 'react';
import { Box, Typography, Button, Stack, Divider, CircularProgress } from '@mui/material';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import { CoordinatePatient } from '~/types/coordinate';
import ProviderCard from './ProviderCard';

interface Props {
  patient: CoordinatePatient;
  onSelectAndConfirm: (providerId: string) => void;
  onSimulateCapacityChange: () => void;
}

const ProviderMatchList: React.FC<Props> = ({
  patient,
  onSelectAndConfirm,
  onSimulateCapacityChange,
}) => {
  const { matchResults, coordinationStatus, selectedProviderId } = patient;

  if (coordinationStatus === 'matching') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, gap: 1.5 }}>
        <CircularProgress size={32} />
        <Typography variant="h6" color="text.secondary">Evaluating providers…</Typography>
        <Typography variant="body2" color="text.secondary">
          Checking insurance, geography, clinical fit, and acceptance likelihood.
        </Typography>
      </Box>
    );
  }

  if (!matchResults || matchResults.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, gap: 1 }}>
        <Typography variant="h6" color="text.secondary">No providers matched</Typography>
        <Typography variant="body2" color="text.secondary">
          Try adjusting the disposition or clinical needs and run matching again.
        </Typography>
      </Box>
    );
  }

  const showSimulate =
    coordinationStatus === 'matched' ||
    coordinationStatus === 'confirm_failed';

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Matched Providers
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {matchResults.length} provider{matchResults.length !== 1 ? 's' : ''} ranked by placement likelihood
          </Typography>
        </Box>
        {showSimulate && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ShuffleIcon />}
            onClick={onSimulateCapacityChange}
          >
            Simulate Capacity Change
          </Button>
        )}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Stack spacing={2}>
        {matchResults.map((match, i) => (
          <ProviderCard
            key={match.provider.id}
            match={match}
            rank={i}
            isSelected={match.provider.id === selectedProviderId}
            coordinationStatus={coordinationStatus}
            onSelectAndConfirm={onSelectAndConfirm}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default ProviderMatchList;
